import { DefaultTheme, useTheme } from 'styled-components'
import Box from '../Box'
import Text from '../Text'
import Icon from '../Icon'
import { ThemeColor } from 'utils/colors'
import { TabContainer, TabContent, SuperScript, NavigationTabVariant, VARIANT_STYLES } from './styles'

type NavigationTabProps = {
  icon?: string
  label?: string
  active?: boolean
  activeTabFontColor?: ThemeColor
  defaultTabLabelColor?: ThemeColor
  superScript?: string
  superScriptColor?: ThemeColor
  variant?: NavigationTabVariant
  fullwidth?: boolean
}

function getVariantConfig(variant: NavigationTabVariant) {
  return VARIANT_STYLES[variant]
}

function resolveTextColor(
  active?: boolean,
  activeTabFontColor?: ThemeColor,
  defaultTabLabelColor?: ThemeColor,
  labelActiveColor?: ThemeColor,
  labelInactiveColor?: ThemeColor,
): ThemeColor {
  if (active && activeTabFontColor) return activeTabFontColor
  if (defaultTabLabelColor) return defaultTabLabelColor
  if (active) return labelActiveColor ?? 'blue1'
  return labelInactiveColor ?? 'disabledText'
}

function resolveBorderColor(theme: DefaultTheme, active?: boolean, inactiveBorderColor?: ThemeColor) {
  if (active) return theme.colors.blue1
  if (inactiveBorderColor) return theme.colors[inactiveBorderColor]
  return 'transparent'
}

const NavigationTab = ({
  icon,
  label,
  active = false,
  activeTabFontColor,
  defaultTabLabelColor,
  superScript,
  superScriptColor,
  variant = 'default',
  fullwidth = false,
}: NavigationTabProps) => {
  const theme = useTheme()
  const config = getVariantConfig(variant)

  const textColor = resolveTextColor(
    active,
    activeTabFontColor,
    defaultTabLabelColor,
    config.labelActiveColor,
    config.labelInactiveColor,
  )
  const borderColor = resolveBorderColor(theme, active, config.inactiveBorderColor)

  return (
    <TabContainer containerPadding={config.containerPadding} fullwidth={fullwidth}>
      <TabContent
        active={active}
        borderColor={borderColor}
        borderWidth={config.borderWidth}
        labelPadding={config.labelPadding}
      >
        {icon && <Icon icon={icon} color={textColor} fontSize={config.labelFontSize} />}
        {label && (
          <Box position="relative">
            <Text
              data-navigation-tab="label"
              color={textColor}
              fontWeight={config.labelFontWeight}
              fontSize={config.labelFontSize}
              isCapitalizedFirstLetter
            >
              {label}
            </Text>
            {superScript && <SuperScript superScriptColor={superScriptColor}>{superScript}</SuperScript>}
          </Box>
        )}
      </TabContent>
    </TabContainer>
  )
}

export default NavigationTab
